import React, { useState, useEffect } from "react";
import { FaMosque, FaQuoteRight } from "react-icons/fa";
import { Link, useNavigate } from "react-router-dom";
import { authLogout } from "../../../Redux/Actions/AuthActions/LogoutAction";
import {
  AiOutlineLogout,
  AiOutlineUser,
  AiOutlineTeam,
  AiOutlineDown,
  AiOutlineRight,
} from "react-icons/ai";
import { BsFillPersonFill, BsCreditCard, BsPeople, BsBook, BsLink45Deg } from "react-icons/bs";
import { useSelector } from "react-redux";
import { MdOutlineFontDownloadOff, MdAnnouncement } from "react-icons/md";
import { HiUsers } from "react-icons/hi";
import { CgPlayListRemove } from "react-icons/cg";
import { GrAnnounce } from "react-icons/gr";
import { HiOutlineUserGroup } from "react-icons/hi";
import { BiBuildingHouse } from "react-icons/bi";
import { RiUserLine } from "react-icons/ri";
import { IoBusinessOutline } from "react-icons/io5";
import Masjidlogo from "../../../Photos/logomasjid.png";
import { RootState } from "../../../Redux/Store";
import PackageJson from "../../../../package.json";

const SideBar = () => {
  interface Admin {
    role: string;
  }
  const sidebarState = useSelector((state: RootState) => state.sidebarState);
  const adminString = localStorage.getItem("admin");
  const admin: Admin | null = adminString ? JSON.parse(adminString) : null;
  const navigate = useNavigate();

  // State for expandable menu items
  const [expandedMenus, setExpandedMenus] = useState<{
    entity: boolean;
    users: boolean;
  }>({
    entity: false,
    users: false,
  });

  // State for responsive behavior
  const [isMobile, setIsMobile] = useState(false);

  // Check window width on mount and resize
  useEffect(() => {
    const checkWidth = () => {
      setIsMobile(window.innerWidth < 768); // 768px is typical mobile breakpoint
    };

    // Check on mount
    checkWidth();

    // Add event listener
    window.addEventListener("resize", checkWidth);

    // Cleanup
    return () => window.removeEventListener("resize", checkWidth);
  }, []);

  const handleLogout = () => {
    authLogout();
    localStorage.clear();
    window.location.reload();
  };

  const toggleMenu = (menuKey: "entity" | "users") => {
    setExpandedMenus((prev) => ({
      ...prev,
      [menuKey]: !prev[menuKey],
    }));
  };

  const MenuItem = ({
    icon,
    text,
    onClick,
    isActive = false,
    iconSize = "w-8 h-8",
    showText = true,
  }: {
    icon: React.ReactNode;
    text: string;
    onClick: () => void;
    isActive?: boolean;
    iconSize?: string;
    showText?: boolean;
  }) => (
    <button
      onClick={onClick}
      className={`
        w-full flex items-center gap-2 px-3 py-2 rounded-lg transition-all duration-300
        ${
          isActive
            ? "bg-gradient-to-r from-green-600 to-green-700 text-white shadow-lg"
            : "text-gray-600 hover:bg-[#054635] hover:text-white"
        }
        ${!showText && "justify-center"}
      `}
    >
      <div className={`${iconSize} flex items-center justify-center`}>
        {icon}
      </div>
      {showText && (
        <span className="font-medium text-sm whitespace-nowrap">{text}</span>
      )}
    </button>
  );

  const ExpandableMenuItem = ({
    icon,
    text,
    isExpanded,
    onToggle,
    children,
    iconSize = "w-8 h-8",
    showText = true,
  }: {
    icon: React.ReactNode;
    text: string;
    isExpanded: boolean;
    onToggle: () => void;
    children: React.ReactNode;
    iconSize?: string;
    showText?: boolean;
  }) => (
    <div className="w-full">
      <button
        onClick={onToggle}
        className={`
          w-full flex items-center justify-between gap-2 px-3 py-2 rounded-lg transition-all duration-300
          text-gray-600 hover:bg-[#054635] hover:text-white
          ${!showText && "justify-center"}
        `}
      >
        <div className="flex items-center gap-3">
          <div className={`${iconSize} flex items-center justify-center`}>
            {icon}
          </div>
          {showText && (
            <span className="font-medium text-sm whitespace-nowrap">
              {text}
            </span>
          )}
        </div>
        {showText && (
          <div className="flex items-center justify-center">
            {isExpanded ? (
              <AiOutlineDown size={16} />
            ) : (
              <AiOutlineRight size={16} />
            )}
          </div>
        )}
      </button>
      {isExpanded && showText && (
        <div className="ml-6 mt-1 space-y-1">{children}</div>
      )}
    </div>
  );

  const SubMenuItem = ({
    icon,
    text,
    onClick,
    isActive = false,
    iconSize = "w-6 h-6",
  }: {
    icon: React.ReactNode;
    text: string;
    onClick: () => void;
    isActive?: boolean;
    iconSize?: string;
  }) => (
    <button
      onClick={onClick}
      className={`
        w-full flex items-center gap-2 px-3 py-1.5 rounded-lg transition-all duration-300 text-sm
        ${
          isActive
            ? "bg-gradient-to-r from-green-600 to-green-700 text-white shadow-lg"
            : "text-gray-600 hover:bg-[#054635] hover:text-white"
        }
      `}
    >
      <div className={`${iconSize} flex items-center justify-center`}>
        {icon}
      </div>
      <span className="font-medium whitespace-nowrap">{text}</span>
    </button>
  );

  // Determine if we should show text based on sidebar state and screen size
  const shouldShowText = sidebarState && !isMobile;

  return (
    <div
      className={`h-screen bg-white border-r border-gray-200 flex flex-col shadow-lg transition-all duration-300 ${
        sidebarState && !isMobile ? "w-38" : "w-12"
      }`}
    >
      {/* Header */}
      <div
        className={`border-b border-gray-100 transition-all duration-300 ${
          shouldShowText ? "p-4" : "p-2"
        }`}
      >
        <Link to="/" className="flex items-center justify-center">
          <img
            src={Masjidlogo}
            alt="Connect Masjid"
            className={`object-contain transition-all duration-300 ${
              shouldShowText ? "h-10 w-auto" : "h-6 w-6"
            }`}
          />
        </Link>
      </div>

      {/* Navigation */}
      <div
        className={`flex-1 space-y-2 overflow-y-auto transition-all duration-300 ${
          shouldShowText ? "p-3" : "p-2"
        }`}
      >
        {shouldShowText ? (
          // Expanded Sidebar
          <div className="space-y-2">
            {/* Entity Section */}
            <ExpandableMenuItem
              icon={<IoBusinessOutline />}
              text="Entity"
              isExpanded={expandedMenus.entity}
              onToggle={() => toggleMenu("entity")}
            >
              <SubMenuItem
                icon={<FaMosque />}
                text="Masjids"
                onClick={() => navigate("/masjids")}
              />
              <SubMenuItem
                icon={<BiBuildingHouse />}
                text="Organizations"
                onClick={() => navigate("/organizations")}
              />
              <SubMenuItem
                icon={<RiUserLine />}
                text="Individuals"
                onClick={() => navigate("/individuals")}
              />
            </ExpandableMenuItem>

            {/* Users Section */}
            {admin?.role === "superadmin" && (
              <ExpandableMenuItem
                icon={<AiOutlineTeam />}
                text="Users"
                isExpanded={expandedMenus.users}
                onToggle={() => toggleMenu("users")}
              >
                <SubMenuItem
                  icon={<HiOutlineUserGroup />}
                  text="External Admins"
                  onClick={() => navigate("/external-admins")}
                />
                <SubMenuItem
                  icon={<BsPeople />}
                  text="Internal Admins"
                  onClick={() => navigate("/internal-admins")}
                />
              </ExpandableMenuItem>
            )}

            {/* Other menu items */}
            {admin?.role === "superadmin" && (
              <>
                <MenuItem
                  icon={<CgPlayListRemove />}
                  text="Freezed Masjids"
                  onClick={() => navigate("/freezeMasjids")}
                />
                <MenuItem
                  icon={<MdOutlineFontDownloadOff />}
                  text="Archives"
                  onClick={() => navigate("/archives")}
                />
                <MenuItem
                  icon={<HiUsers />}
                  text="End User"
                  onClick={() => navigate("/end-users")}
                />
                <MenuItem
                  icon={<MdAnnouncement />}
                  text="Announcement"
                  onClick={() => navigate("/announcements")}
                />
                <MenuItem
                  icon={<BsBook />}
                  text="Hadith"
                  onClick={() => navigate("/hadith")}
                />
                <MenuItem
                  icon={<FaQuoteRight />}
                  text="Islamic Quotes"
                  onClick={() => navigate("/quran-verses")}
                />
                <MenuItem
                  icon={<BsCreditCard />}
                  text="Payment Center"
                  onClick={() => navigate("/verification-center")}
                />
                <MenuItem
                  icon={<BsLink45Deg />}
                  text="Donation Link Review"
                  onClick={() => navigate("/donation-link-review")}
                />
              </>
            )}
          </div>
        ) : (
          // Collapsed Sidebar (Mobile or collapsed state)
          <div className="space-y-4">
            {/* Entity - Masjids */}
            <MenuItem
              icon={<FaMosque />}
              text=""
              onClick={() => navigate("/masjids")}
              showText={false}
            />

            {/* Entity - Organizations */}
            <MenuItem
              icon={<BiBuildingHouse />}
              text=""
              onClick={() => navigate("/organizations")}
              showText={false}
            />

            {/* Entity - Individuals */}
            <MenuItem
              icon={<RiUserLine />}
              text=""
              onClick={() => navigate("/individuals")}
              showText={false}
            />

            {/* Users - External Admins */}
            {admin?.role === "superadmin" && (
              <MenuItem
                icon={<HiOutlineUserGroup />}
                text=""
                onClick={() => navigate("/external-admins")}
                showText={false}
              />
            )}

            {/* Users - Internal Admins */}
            {admin?.role === "superadmin" && (
              <MenuItem
                icon={<BsPeople />}
                text=""
                onClick={() => navigate("/internal-admins")}
                showText={false}
              />
            )}

            {/* Other menu items */}
            {admin?.role === "superadmin" && (
              <>
                <MenuItem
                  icon={<CgPlayListRemove />}
                  text=""
                  onClick={() => navigate("/freezeMasjids")}
                  showText={false}
                />
                <MenuItem
                  icon={<HiUsers />}
                  text=""
                  onClick={() => navigate("/end-users")}
                  showText={false}
                />
                <MenuItem
                  icon={<MdAnnouncement />}
                  text=""
                  onClick={() => navigate("/announcements")}
                  showText={false}
                />
                <MenuItem
                  icon={<BsBook />}
                  text=""
                  onClick={() => navigate("/hadith")}
                  showText={false}
                />
                <MenuItem
                  icon={<BsCreditCard />}
                  text=""
                  onClick={() => navigate("/verification-center")}
                  showText={false}
                />
              </>
            )}

            {(admin?.role === "superadmin" || admin?.role === "admin") && (
              <>
                <MenuItem
                  icon={<AiOutlineUser />}
                  text=""
                  onClick={() => navigate("/admins")}
                  showText={false}
                />
                <MenuItem
                  icon={<MdOutlineFontDownloadOff />}
                  text=""
                  onClick={() => navigate("/freezeAdmins")}
                  showText={false}
                />
              </>
            )}
          </div>
        )}
      </div>

      {/* Footer */}
      <div
        className={`border-t border-gray-100 transition-all duration-300 ${
          shouldShowText ? "p-3" : "p-2"
        }`}
      >
        <MenuItem
          icon={<AiOutlineLogout />}
          text={shouldShowText ? "Logout" : ""}
          onClick={handleLogout}
          showText={shouldShowText}
        />

        <div className="mt-4 text-center">
          <span
            className={`text-xs text-gray-400 font-mono transition-all duration-300 ${
              shouldShowText ? "opacity-100" : "opacity-0"
            }`}
          >
            v{PackageJson.version}
          </span>
        </div>
      </div>
    </div>
  );
};

export default SideBar;
